'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import PayrollHeader from './components/PayrollHeader';
import PayrollSummaryCards from './components/PayrollSummaryCards';
import EarningsSummaryTable from './components/EarningsSummaryTable';
import DeductionTracking from './components/DeductionTracking';
import PayPeriodComparison from './components/PayPeriodComparison';

export type PayrollTab = 'earnings' | 'deductions' | 'comparison';

export interface StaffEarning {
  id: string;
  staffName: string;
  role: string;
  department: string;
  regularHours: number;
  overtimeHours: number;
  regularRate: number;
  overtimeRate: number;
  regularPay: number;
  overtimePay: number;
  grossPay: number;
  deductions: number;
  netPay: number;
  shifts: number;
}

export interface Deduction {
  id: string;
  staffName: string;
  role: string;
  federalTax: number;
  stateTax: number;
  socialSecurity: number;
  medicare: number;
  healthInsurance: number;
  retirement401k: number;
  other: number;
  totalDeductions: number;
  grossPay: number;
}

export interface PayPeriod {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
  totalStaff: number;
  totalHours: number;
  totalGross: number;
  totalDeductions: number;
  totalNet: number;
  overtimeHours: number;
  overtimeCost: number;
}

export const PAY_PERIODS: PayPeriod[] = [
  { id: 'pp-1', label: 'Jul 1–15, 2024', startDate: '2024-07-01', endDate: '2024-07-15', totalStaff: 24, totalHours: 1840, totalGross: 112450, totalDeductions: 28112, totalNet: 84338, overtimeHours: 124, overtimeCost: 9920 },
  { id: 'pp-2', label: 'Jul 16–31, 2024', startDate: '2024-07-16', endDate: '2024-07-31', totalStaff: 26, totalHours: 1960, totalGross: 121800, totalDeductions: 30450, totalNet: 91350, overtimeHours: 148, overtimeCost: 11840 },
  { id: 'pp-3', label: 'Jun 16–30, 2024', startDate: '2024-06-16', endDate: '2024-06-30', totalStaff: 22, totalHours: 1720, totalGross: 104200, totalDeductions: 26050, totalNet: 78150, overtimeHours: 96, overtimeCost: 7680 },
  { id: 'pp-4', label: 'Jun 1–15, 2024', startDate: '2024-06-01', endDate: '2024-06-15', totalStaff: 21, totalHours: 1680, totalGross: 98600, totalDeductions: 24650, totalNet: 73950, overtimeHours: 80, overtimeCost: 6400 },
];

export const STAFF_EARNINGS: StaffEarning[] = [
  { id: 'se-1', staffName: 'Maria Santos', role: 'RN', department: 'ICU', regularHours: 72, overtimeHours: 12, regularRate: 65, overtimeRate: 97.5, regularPay: 4680, overtimePay: 1170, grossPay: 5850, deductions: 1462, netPay: 4388, shifts: 6 },
  { id: 'se-2', staffName: 'James Okafor', role: 'CNA', department: 'Med-Surg', regularHours: 80, overtimeHours: 8, regularRate: 28, overtimeRate: 42, regularPay: 2240, overtimePay: 336, grossPay: 2576, deductions: 644, netPay: 1932, shifts: 10 },
  { id: 'se-3', staffName: 'Priya Nair', role: 'LPN', department: 'ER', regularHours: 76, overtimeHours: 4, regularRate: 42, overtimeRate: 63, regularPay: 3192, overtimePay: 252, grossPay: 3444, deductions: 861, netPay: 2583, shifts: 8 },
  { id: 'se-4', staffName: 'Derek Williams', role: 'RN', department: 'OR', regularHours: 80, overtimeHours: 16, regularRate: 68, overtimeRate: 102, regularPay: 5440, overtimePay: 1632, grossPay: 7072, deductions: 1768, netPay: 5304, shifts: 7 },
  { id: 'se-5', staffName: 'Aisha Johnson', role: 'NP', department: 'Urgent Care', regularHours: 80, overtimeHours: 0, regularRate: 95, overtimeRate: 142.5, regularPay: 7600, overtimePay: 0, grossPay: 7600, deductions: 1900, netPay: 5700, shifts: 10 },
  { id: 'se-6', staffName: 'Carlos Mendez', role: 'CNA', department: 'Rehab', regularHours: 64, overtimeHours: 0, regularRate: 28, overtimeRate: 42, regularPay: 1792, overtimePay: 0, grossPay: 1792, deductions: 448, netPay: 1344, shifts: 8 },
  { id: 'se-7', staffName: 'Linda Park', role: 'RN', department: 'Pediatrics', regularHours: 72, overtimeHours: 8, regularRate: 66, overtimeRate: 99, regularPay: 4752, overtimePay: 792, grossPay: 5544, deductions: 1386, netPay: 4158, shifts: 6 },
  { id: 'se-8', staffName: 'Robert Chen', role: 'MD', department: 'ER', regularHours: 80, overtimeHours: 20, regularRate: 145, overtimeRate: 217.5, regularPay: 11600, overtimePay: 4350, grossPay: 15950, deductions: 3987, netPay: 11963, shifts: 10 },
  { id: 'se-9', staffName: 'Fatima Al-Hassan', role: 'LPN', department: 'ICU', regularHours: 72, overtimeHours: 0, regularRate: 43, overtimeRate: 64.5, regularPay: 3096, overtimePay: 0, grossPay: 3096, deductions: 774, netPay: 2322, shifts: 6 },
  { id: 'se-10', staffName: 'Marcus Thompson', role: 'RN', department: 'Oncology', regularHours: 80, overtimeHours: 12, regularRate: 70, overtimeRate: 105, regularPay: 5600, overtimePay: 1260, grossPay: 6860, deductions: 1715, netPay: 5145, shifts: 8 },
];

export const DEDUCTIONS_DATA: Deduction[] = STAFF_EARNINGS.map((s) => {
  const federal = Math.round(s.grossPay * 0.12);
  const state = Math.round(s.grossPay * 0.05);
  const ss = Math.round(s.grossPay * 0.062);
  const medicare = Math.round(s.grossPay * 0.0145);
  const health = s.role === 'MD' || s.role === 'NP' ? 320 : 180;
  const retirement = Math.round(s.grossPay * 0.03);
  const other = 25;
  const total = federal + state + ss + medicare + health + retirement + other;
  return {
    id: `ded-${s.id}`,
    staffName: s.staffName,
    role: s.role,
    federalTax: federal,
    stateTax: state,
    socialSecurity: ss,
    medicare,
    healthInsurance: health,
    retirement401k: retirement,
    other,
    totalDeductions: total,
    grossPay: s.grossPay,
  };
});

export default function PayrollPage() {
  const [activeTab, setActiveTab] = useState<PayrollTab>('earnings');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('pp-2');

  const currentPeriod = PAY_PERIODS.find((p) => p.id === selectedPeriod) ?? PAY_PERIODS[1];

  return (
    <AppLayout>
      <div className="space-y-5 fade-in">
        <PayrollHeader
          activeTab={activeTab}
          onTabChange={setActiveTab}
          selectedPeriod={selectedPeriod}
          onPeriodChange={setSelectedPeriod}
          periods={PAY_PERIODS}
          currentPeriod={currentPeriod}
        />
        <PayrollSummaryCards period={currentPeriod} />
        {activeTab === 'earnings' && (
          <EarningsSummaryTable earnings={STAFF_EARNINGS} period={currentPeriod} />
        )}
        {activeTab === 'deductions' && (
          <DeductionTracking deductions={DEDUCTIONS_DATA} period={currentPeriod} />
        )}
        {activeTab === 'comparison' && (
          <PayPeriodComparison periods={PAY_PERIODS} currentPeriodId={selectedPeriod} />
        )}
      </div>
    </AppLayout>
  );
}
